import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

type RewardsModalProps = {
    open: boolean;
    onClose: () => void;
};

type RewardItem = {
    name: string;
    qty: number;
    images: string[];
};

const rewards: RewardItem[] = [
    {
        name: 'Put na Lefkadu 2 osobe',
        qty: 1,
        images: [
            '/assets/novi_pokloni/putovanje (1).png',
            '/assets/novi_pokloni/putovanje (2).png',
            '/assets/novi_pokloni/putovanje (3).png',
        ],
    },
    {
        name: 'Trotinet električni',
        qty: 1,
        images: [
            '/assets/novi_pokloni/elektricni trotinet (1).webp',
            '/assets/novi_pokloni/elektricni trotinet (2).webp',
            '/assets/novi_pokloni/elektricni trotinet (3).webp',
            '/assets/novi_pokloni/elektricni trotinet (4).webp',
            '/assets/novi_pokloni/elektricni trotinet (5).webp',
        ],
    },
    {
        name: 'NESPRESSO veliki',
        qty: 1,
        images: [
            '/assets/novi_pokloni/nespresso aparat (1).webp',
            '/assets/novi_pokloni/nespresso aparat (2).webp',
            '/assets/novi_pokloni/nespresso aparat (3).webp',
        ],
    },
    {
        name: 'NESPRESSO mali',
        qty: 2,
        images: [
            '/assets/novi_pokloni/nespresso aparat (1).webp',
            '/assets/novi_pokloni/nespresso aparat (2).webp',
            '/assets/novi_pokloni/nespresso aparat (3).webp',
        ],
    },
    {
        name: 'Airfryer',
        qty: 1,
        images: [
            '/assets/novi_pokloni/airfryer (1).webp',
            '/assets/novi_pokloni/airfryer (2).webp',
            '/assets/novi_pokloni/airfryer (3).webp',
            '/assets/novi_pokloni/airfryer (4).webp',
        ],
    },
    {
        name: 'Dehidrator',
        qty: 1,
        images: [
            '/assets/novi_pokloni/dehidrator (1).jpg',
            '/assets/novi_pokloni/dehidrator (2).jpg',
        ],
    },
    {
        name: 'Mikser činija veliki',
        qty: 3,
        images: [
            '/assets/novi_pokloni/ariete mikser (1).jpg',
            '/assets/novi_pokloni/ariete mikser (2).jpg',
            '/assets/novi_pokloni/ariete mikser (3).jpg',
        ],
    },
    {
        name: 'Mikser ručni',
        qty: 3,
        images: [
            '/assets/novi_pokloni/rosmarino_mikser (1).webp',
            '/assets/novi_pokloni/rosmarino_mikser (2).webp',
        ],
    },
    {
        name: 'Aparat za palačinke',
        qty: 1,
        images: [
            '/assets/novi_pokloni/ariete palacinke (1).jpg',
            '/assets/novi_pokloni/ariete palacinke (2).jpg',
            '/assets/novi_pokloni/ariete palacinke (3).jpg',
        ],
    },
    {
        name: 'Džezva metalac',
        qty: 6,
        images: [
            '/assets/novi_pokloni/dzezve (1).jpg',
            '/assets/novi_pokloni/dzezve (2).jpg',
        ],
    },
    {
        name: 'Bluetooth slušalice',
        qty: 5,
        images: [
            '/assets/novi_pokloni/slusalice.jpeg',
        ],
    },
    {
        name: 'Majice Promeks',
        qty: 20,
        images: [
            '/assets/novi_pokloni/promex_paket_decembar.png',
        ],
    },
    {
        name: 'Šoljica za kafu',
        qty: 15,
        images: [
            '/assets/novi_pokloni/soljice.jpeg',
        ],
    },
];

const ImageSlider: React.FC<{ images: string[]; alt: string }> = ({ images, alt }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    const goToPrevious = () => {
        setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    };

    const goToNext = () => {
        setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    };

    if (images.length === 1) {
        return (
            <img
                src={images[0]}
                alt={alt}
                className="w-72 h-auto rounded-2xl shadow-2xl border-4 border-amber-100"
            />
        );
    }

    return (
        <div className="relative">
            <img
                src={images[currentIndex]}
                alt={`${alt} - ${currentIndex + 1}`}
                className="w-72 h-auto rounded-2xl shadow-2xl border-4 border-amber-100"
            />

            {/* Left Arrow */}
            <button
                onClick={goToPrevious}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 bg-white hover:bg-amber-100 text-amber-900 rounded-full w-10 h-10 flex items-center justify-center shadow-lg border-2 border-amber-200 transition-colors"
            >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                </svg>
            </button>

            {/* Right Arrow */}
            <button
                onClick={goToNext}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 bg-white hover:bg-amber-100 text-amber-900 rounded-full w-10 h-10 flex items-center justify-center shadow-lg border-2 border-amber-200 transition-colors"
            >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                </svg>
            </button>

            {/* Dots Indicator */}
            <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
                {images.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => setCurrentIndex(index)}
                        className={`w-2.5 h-2.5 rounded-full transition-colors ${
                            index === currentIndex ? 'bg-amber-600' : 'bg-amber-300'
                        }`}
                    />
                ))}
            </div>
        </div>
    );
};

const RewardsModal: React.FC<RewardsModalProps> = ({ open, onClose }) => {
    useEffect(() => {
        if (open) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [open]);

    const QuantityBadge = ({ qty }: { qty: number }) => {
        if (qty <= 1) return null;
        return (
            <div className="absolute -top-4 -right-4 bg-red-600 text-white font-bold rounded-full w-14 h-14 flex items-center justify-center shadow-xl text-xl z-10 border-4 border-white">
                ×{qty}
            </div>
        );
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 bg-black bg-opacity-60 overflow-y-auto"
                >
                    <div className="min-h-screen flex items-start justify-center px-4 py-10">
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            transition={{ duration: 0.3, ease: 'easeOut' }}
                            className="bg-white rounded-2xl max-w-2xl w-full relative shadow-2xl overflow-hidden"
                        >
                            {/* Header */}
                            <div className="bg-gradient-to-r from-amber-100 to-amber-50 px-6 py-6 border-b-2 border-amber-300">
                                <h2 className="text-3xl lg:text-4xl font-bold text-amber-900 text-center tracking-wider">
                                    POKLONI
                                </h2>
                                <button
                                    onClick={onClose}
                                    className="absolute top-5 right-6 text-4xl font-bold text-amber-900 hover:text-amber-700"
                                >
                                    ×
                                </button>
                            </div>

                            {/* Sadržaj */}
                            <div className="px-6 py-10 space-y-16 overflow-y-auto max-h-screen pb-32">
                                {rewards.map((reward, index) => (
                                    <div key={index} className="flex flex-col items-center">
                                        <div className="relative">
                                            <QuantityBadge qty={reward.qty} />
                                            <ImageSlider images={reward.images} alt={reward.name} />
                                        </div>
                                        <p className="mt-8 text-xl font-bold text-gray-800">{reward.name}</p>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default RewardsModal;
